import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';

interface CommentListProps {
  prayerId: string;
  currentUserId?: string;
  showForm?: boolean;
  maxHeight?: string;
}

export default function CommentList({
  prayerId,
  currentUserId,
  showForm = true,
  maxHeight
}: CommentListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading, error } = useQuery({
    queryKey: ['comments', prayerId],
    queryFn: async () => {
      // First get comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('prayer_comments')
        .select('*')
        .eq('prayer_id', prayerId)
        .is('parent_id', null)
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      // Then get profiles and reactions separately
      const commentsWithProfiles = await Promise.all(
        (commentsData || []).map(async (comment) => {
          // Get profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, first_name, last_name, avatar_url')
            .eq('id', comment.user_id)
            .single();

          // Get reactions
          const { data: reactions } = await supabase
            .from('comment_reactions')
            .select('*')
            .eq('comment_id', comment.id);

          // Get replies
          const { data: replies } = await supabase
            .from('prayer_comments')
            .select('*')
            .eq('parent_id', comment.id)
            .eq('is_approved', true)
            .order('created_at', { ascending: true });

          // Get profiles and reactions for replies
          const repliesWithProfiles = await Promise.all(
            (replies || []).map(async (reply) => {
              const { data: replyProfile } = await supabase
                .from('profiles')
                .select('username, first_name, last_name, avatar_url')
                .eq('id', reply.user_id)
                .single();

              const { data: replyReactions } = await supabase
                .from('comment_reactions')
                .select('*')
                .eq('comment_id', reply.id);

              return {
                ...reply,
                profiles: replyProfile,
                comment_reactions: replyReactions || [],
                attachments: (reply.attachments as any[]) || []
              };
            })
          );

          return {
            ...comment,
            profiles: profile,
            comment_reactions: reactions || [],
            replies: repliesWithProfiles,
            attachments: (comment.attachments as any[]) || []
          };
        })
      );

      return commentsWithProfiles;
    },
    enabled: !!prayerId
  });

  const addCommentMutation = useMutation({
    mutationFn: async ({ content, parentId, attachments }: { content: string; parentId?: string; attachments?: any[] }) => {
      if (!currentUserId) throw new Error('Must be logged in to comment');

      const { data, error } = await supabase
        .from('prayer_comments')
        .insert({
          prayer_id: prayerId,
          user_id: currentUserId,
          content,
          parent_id: parentId || null,
          attachments: attachments || []
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', prayerId] });
      toast({
        title: "Comment posted",
        description: "Your comment has been posted successfully.",
      });
    },
    onError: (error) => {
      console.error('Error posting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to post comment. Please try again.",
      });
    }
  });

  const reactMutation = useMutation({
    mutationFn: async ({ commentId, type }: { commentId: string; type: string }) => {
      if (!currentUserId) throw new Error('Must be logged in to react');

      const { data, error } = await supabase
        .from('comment_reactions')
        .insert({
          comment_id: commentId,
          user_id: currentUserId,
          reaction_type: type
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', prayerId] });
    },
    onError: (error) => {
      console.error('Error adding reaction:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add reaction. Please try again.",
      });
    }
  });

  const removeReactionMutation = useMutation({
    mutationFn: async ({ commentId, type }: { commentId: string; type: string }) => {
      if (!currentUserId) throw new Error('Must be logged in to remove reaction');

      const { error } = await supabase
        .from('comment_reactions')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', currentUserId)
        .eq('reaction_type', type);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', prayerId] });
    },
    onError: (error) => {
      console.error('Error removing reaction:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove reaction. Please try again.",
      });
    }
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from('prayer_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', currentUserId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', prayerId] });
      toast({
        title: "Comment deleted",
        description: "Your comment has been deleted.",
      });
    },
    onError: (error) => {
      console.error('Error deleting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete comment. Please try again.",
      });
    }
  });

  const handleAddComment = async (content: string, attachments?: any[]) => {
    await addCommentMutation.mutateAsync({ content, attachments });
  };

  const handleReply = async (parentId: string, content: string, attachments?: any[]) => {
    await addCommentMutation.mutateAsync({ content, parentId, attachments });
  };

  const handleReact = async (commentId: string, type: string) => {
    await reactMutation.mutateAsync({ commentId, type });
  };

  const handleRemoveReaction = async (commentId: string, type: string) => {
    await removeReactionMutation.mutateAsync({ commentId, type });
  };

  const handleReport = (commentId: string) => {
    // TODO: Implement report functionality
    toast({
      title: "Report submitted",
      description: "Thank you for your report. We'll review it shortly.",
    });
  };

  const handleDelete = async (commentId: string) => {
    await deleteCommentMutation.mutateAsync(commentId);
  };

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
        <p>Failed to load comments. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      <ScrollArea 
        className="flex-1" 
        style={maxHeight ? { maxHeight } : undefined}
      >
        <div className="space-y-4 p-4">
          {isLoading ? (
            <div className="text-center py-8">
              <Loader2 size={32} className="mx-auto mb-2 animate-spin" />
              <p className="text-muted-foreground">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
              <p>No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={handleReply}
                onReact={handleReact}
                onRemoveReaction={handleRemoveReaction}
                onReport={handleReport}
                onDelete={handleDelete}
                currentUserId={currentUserId}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {showForm && currentUserId && (
        <div className="border-t pt-4 px-4">
          <CommentForm
            onSubmit={handleAddComment}
            placeholder="Share your thoughts on this prayer..."
            buttonText="Post Comment"
          />
        </div>
      )}
    </div>
  );
}